import React, { useMemo, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Button, formatLocale } from '@plerionhq/design-system';
import { Select } from '@plerionhq/design-system/experimental';
import {
  useRoiReportByMonth,
  useGenerateRoiReport,
  getSupportedRoiMonthOptions,
  RoiGenerationInProgressError,
} from '../../../../../api/pleri-roi-reports';
import type {
  GenerateRoiReportResponse,
  RoiReportDashboardSchema,
} from '../../../../../api/pleri-roi-reports';
import { UniversalDashboardRenderer } from '../../../../../dashboard/components/common/UniversalDashboardRenderer';
import type { SimpleDashboardConfig } from '../../../../../dashboard/components/Dashboard/Dashboard';
import { RoiReportMeta } from './RoiReportMeta';
import { RoiReportMarkdown } from './RoiReportMarkdown';
import {
  formatMonthLabel,
  isGenerationStale,
  isGenerationLongRunning,
  formatStartedAt,
} from './roiReportUtils';

import { useTranslation } from 'react-i18next';

const POLL_INTERVAL_MS = 20000;

function isDashboardSchema(
  value: RoiReportDashboardSchema | null | undefined,
): value is RoiReportDashboardSchema {
  if (value == null || typeof value !== 'object') {
    return false;
  }
  return 'sections' in value && Array.isArray(value.sections);
}

interface RoiReportDetailProps {
  month: string | undefined;
}

export const RoiReportDetail: React.FC<RoiReportDetailProps> = ({ month }) => {
  const { t } = useTranslation('pleri/settings');
  const navigate = useNavigate();
  const generateMutation = useGenerateRoiReport();
  const {
    data: report,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useRoiReportByMonth(
    month ?? '',
    typeof month === 'string' && month.length > 0,
  );

  const monthOptions = useMemo(() => getSupportedRoiMonthOptions(), []);
  const selectOptions = useMemo(() => {
    const base = monthOptions.map((o) => ({ label: o.label, value: o.value }));
    if (
      month &&
      /^\d{4}-\d{2}$/.test(month) &&
      !base.some((o) => o.value === month)
    ) {
      const [y, m] = month.split('-').map(Number);
      if (y != null && m != null && (y > 2026 || (y === 2026 && m >= 2))) {
        const d = new Date(y, m - 1, 1);
        base.push({
          label: d.toLocaleDateString(formatLocale(), {
            month: 'long',
            year: 'numeric',
          }),
          value: month,
        });
      }
    }
    return base;
  }, [monthOptions, month]);

  const inProgress =
    report != null &&
    report.generationStatus === 'in_progress' &&
    !isGenerationStale(report);
  const longRunning = report != null && isGenerationLongRunning(report);

  useEffect(() => {
    if (!inProgress) {
      return;
    }
    const id = setInterval(() => {
      refetch();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [inProgress, refetch]);

  const handleMonthChange = (selected: { label: string; value: string }) => {
    navigate({
      to: '/settings/roi-reports/$month',
      params: { month: selected.value },
    });
  };

  const handleGenerateOrRegenerate = () => {
    if (!month) {
      return;
    }
    generateMutation.mutate(
      { month },
      {
        onSuccess: (data: GenerateRoiReportResponse) => {
          toast.success(
            t('reportGenerationQueuedFor', {
              month: formatMonthLabel(data.month),
            }),
          );
          refetch();
        },
        onError: (err) => {
          if (err instanceof RoiGenerationInProgressError) {
            toast.success(t('reportGenerationIsAlreadyInProgressFor'));
            refetch();
            return;
          }
          const message =
            err instanceof Error ? err.message : t('failedToQueueReport');
          toast.error(message);
        },
      },
    );
  };

  const generateDisabled =
    generateMutation.isPending || (inProgress && !generateMutation.isPending);

  if (month == null || (typeof month === 'string' && month.trim() === '')) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-800">
        <p className="text-gray-600 dark:text-gray-400">
          {t('invalidReportMonth')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="h-10 w-48 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="flex gap-4">
            <div className="h-10 w-[200px] animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-10 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        </header>
        <div className="space-y-4">
          <div className="h-6 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-40 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
          <div className="h-40 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-700 dark:bg-red-950">
        {error instanceof Error ? error.message : t('failedToLoadReport')}
      </div>
    );
  }

  const hasReport = report != null;
  const hasDashboard = hasReport && isDashboardSchema(report.dashboardSchema);
  const hasMarkdown =
    hasReport &&
    report.report != null &&
    typeof report.report === 'string' &&
    report.report.trim() !== '';
  const completedWithNoContent =
    hasReport &&
    report.generationStatus !== 'in_progress' &&
    !hasDashboard &&
    !hasMarkdown;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100 leading-tight">
            {t('roiReports')}
          </h1>
          <p className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            {t('viewAndGenerateMonthlyRoiReportsReports')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('month')}
            </span>
            <Select
              options={selectOptions}
              defaultValue={month}
              onSelect={handleMonthChange}
              placeholder={t('selectMonth')}
              className="min-w-[200px]"
            />
          </label>
          <div className="flex flex-col items-end gap-1">
            <span className="text-sm font-medium text-transparent select-none" aria-hidden="true">&nbsp;</span>
            <Button
              onClick={handleGenerateOrRegenerate}
              isDisabled={generateDisabled}
              variant="primary"
              size="small"
              isLoading={generateMutation.isPending}
              icon={<ArrowRightIcon className="h-4 w-4" />}
              iconPosition="trailing"
              title={
                generateDisabled && !generateMutation.isPending
                  ? t('reportIsAlreadyGeneratingForThisMonth')
                  : undefined
              }
              label={
                hasReport && !inProgress ? t('regenerate') : t('generateReport')
              }
            />
            {inProgress && isFetching && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('checkingForUpdates')}
              </span>
            )}
          </div>
        </div>
      </header>
      {!hasReport ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <p className="text-gray-600 dark:text-gray-400">
            {t('noReportForThisMonthYet')}
          </p>
        </div>
      ) : inProgress ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-8 dark:border-amber-700 dark:bg-amber-950/50">
          <div className="flex items-center gap-2 text-lg font-medium text-gray-900 dark:text-gray-100">
            <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
            <span>{t('generating')}</span>
          </div>
          {report.generationStartedAt && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {formatStartedAt(report.generationStartedAt)}
            </p>
          )}
          <p className="mt-4 text-gray-700 dark:text-gray-300">
            {t('yourReportIsBeingGeneratedThisUsually')}
          </p>
          {longRunning && (
            <p className="mt-2 text-sm text-amber-800">
              {t('generationStartedOverAnHourAgoIf')}
            </p>
          )}
        </div>
      ) : completedWithNoContent ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center dark:border-amber-700 dark:bg-amber-950">
          <p className="text-gray-700 dark:text-gray-300">
            {t('generationFailedOrProducedNoContent')}
          </p>
          <Button
            variant="primary"
            className="mt-4"
            onClick={handleGenerateOrRegenerate}
            isDisabled={generateMutation.isPending}
            isLoading={generateMutation.isPending}
            label={t('tryAgain')}
          />
        </div>
      ) : (
        <>
          <RoiReportMeta report={report} />
          {hasDashboard ? (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
              <UniversalDashboardRenderer
                config={report.dashboardSchema as SimpleDashboardConfig}
                variant="full"
                enableScreenshot
                enablePDF
                showAskPleri={false}
              />
            </div>
          ) : hasMarkdown ? (
            <RoiReportMarkdown
              content={report.report as string}
              month={report.month}
            />
          ) : null}
        </>
      )}
    </div>
  );
};
